/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Dispatch } from '@reduxjs/toolkit';
import Header from 'components/Header/Header';
import { ValidationErrors } from 'final-form';
import { Component } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AppDispatch, AppState } from 'store';
import { resetPassword, verifyResetPasswordToken } from 'store/auth/auth.actions';
import { getAuthSpinnerSelector } from 'store/spinner/spinner.selectors';
import { comparePass, composeValidators, password, required } from 'utils/customValidators';
import { VerifyTokenDTO } from 'utils/restApplicationClient';
import { ChangePasswordDTO } from 'utils/restApplicationClientTypeOverrides';
import CustomField from '../Input/CustomField';
import { LoginFormContainer, LoginFormPageContainer, LoginFormTopBar } from '../Login/LoginForm.styled';

interface IFormValues {
  newPassword: string;
  confirmPassword: string;
}

interface IMatchProps {
  token: string;
}

interface IStateProps {
  spinner: boolean;
}

interface IDispatchProps {
  resetPassword: (payload: ChangePasswordDTO) => AppDispatch;
  verifyResetPasswordToken: (payload: VerifyTokenDTO) => AppDispatch;
}

type PropType = WithTranslation & RouteComponentProps<IMatchProps> & IDispatchProps & IStateProps;
class ResetPasswordForm extends Component<PropType> {
  componentDidMount(): void {
    const {
      verifyResetPasswordToken,
      location: { search },
    } = this.props;
    verifyResetPasswordToken({ token: new URLSearchParams(search).get('token') || '' });
  }

  handleSubmit = ({ newPassword }: IFormValues): void => {
    const {
      resetPassword,
      location: { search },
    } = this.props;
    resetPassword({
      newPassword,
      resetPasswordToken: new URLSearchParams(search).get('token') || '',
    });
  };

  render(): JSX.Element {
    const { t, spinner } = this.props;

    return (
      <LoginFormPageContainer>
        <Header />
        <Box display="flex" alignItems="center" marginTop="144px" flexDirection="column">
          <LoginFormContainer>
            <LoginFormTopBar>
              <Typography color="textSecondary" component="div">
                <Box fontWeight="fontWeightBold" color="#748392">
                  {t('loginForm.resetPassword')}
                </Box>
              </Typography>
            </LoginFormTopBar>
            <Box padding="12px 24px">
              <FinalForm
                onSubmit={this.handleSubmit}
                validate={(values): ValidationErrors | undefined => comparePass(values)}
                subscription={{ submitting: true, pristine: true }}
                render={({ handleSubmit }): JSX.Element => (
                  <form onSubmit={handleSubmit}>
                    <Field
                      name="newPassword"
                      component={CustomField}
                      validate={composeValidators([required, password])}
                      label="loginForm.labels.newPassword"
                      textFieldProps={{ type: 'password' }}
                    />

                    <Field
                      name="confirmPassword"
                      component={CustomField}
                      validate={composeValidators([required, password])}
                      label="loginForm.labels.confirmPassword"
                      textFieldProps={{ type: 'password' }}
                    />
                    <Box marginTop="18px">
                      <Button fullWidth variant="contained" color="secondary" type="submit" disabled={spinner}>
                        {spinner ? <CircularProgress size={24} color="secondary" /> : t('loginForm.resetPassword')}
                      </Button>
                    </Box>
                  </form>
                )}
              />
            </Box>
          </LoginFormContainer>
        </Box>
      </LoginFormPageContainer>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  spinner: getAuthSpinnerSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  resetPassword: (payload: ChangePasswordDTO): AppDispatch => dispatch(resetPassword(payload)),
  verifyResetPasswordToken: (payload: VerifyTokenDTO): AppDispatch => dispatch(verifyResetPasswordToken(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(ResetPasswordForm)));
